import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "Is depression at 60 just a normal part of aging?",
    answer:
      "No. Depression is not a normal part of aging. Most people in their sixties are not depressed. The ones who are have a treatable condition. The exhaustion, flatness, withdrawal, and physical complaints that haven't responded to treatment are symptoms of an illness, not the inevitable texture of your sixties. Treatment works at this age as well as it works at any other.",
  },
  {
    question: "Could my memory problems be depression instead of dementia?",
    answer:
      "Very possibly. Depression in older adults often causes memory and concentration problems that look like cognitive decline, and they typically improve when the depression is treated. It does not mean you're losing your mind or that it's the beginning of dementia. A proper evaluation can distinguish between the two.",
  },
  {
    question: "Why does depression show up at 60?",
    answer:
      "Retirement removes the structure and identity that work provided for forty years. Friends and siblings begin to die. Health changes accumulate — heart disease, diabetes, chronic pain — and the medications for each, some of which contribute to depression. Adult children are busy. Isolation creeps in. If depression has run in your family or you had episodes earlier in life, the accumulated losses can bring it back.",
  },
  {
    question: "What if medication causes too many side effects at my age?",
    answer:
      "Older adults are more likely to have depression that doesn't fully respond to medication, and more likely to have side effects that make it hard to tolerate — dizziness, falls, confusion, stomach trouble. If you've had two adequate trials without relief, adding a third has about a 14 percent chance of remission while adding to the side-effect burden. SAINT is a non-drug option: no anesthesia, nothing in your bloodstream, no interaction with your other medications, and it takes five days.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "Diagnosed With Depression at 50: You Didn't Wait Too Long. You're Right on Time.",
    href: "/diagnosed-with-depression-at-50",
    description:
      "Why 50 is a turning point, what not to accept, and the treatment that didn't exist when you first felt this way.",
  },
  {
    title:
      "Diagnosed With Depression at 70: You Have Every Right to Feel Better",
    href: "/diagnosed-with-depression-at-70",
    description:
      "Why depression at 70 is not a normal part of aging — and what the years ahead can hold.",
  },
  {
    title:
      "High-Functioning Depression: When Holding It Together Is the Problem",
    href: "/high-functioning-depression-when-holding-it-together-is-the-problem",
    description:
      "When functioning hides the severity of depression — and why partial relief isn't the goal.",
  },
  {
    title: "How SAINT® Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    description:
      "A side-by-side comparison of SAINT® against medication, standard TMS, esketamine, and ECT.",
  },
];

export default function DiagnosedDepressionAt60() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "Diagnosed With Depression at 60? This Is Not Just Getting Older",
        description:
          "At 60, depression is the most misdiagnosed and undertreated it will ever be. What it means, why 60 is common, what to do when medication isn't enough, and a non-drug option that fits an older adult's life.",
        canonical: "/diagnosed-with-depression-at-60",
        keywords:
          "diagnosed with depression at 60, major depressive disorder sixties, treatment-resistant depression older adult, SAINT TMS, depression not aging, memory problems depression, non-drug depression treatment, fMRI-guided TMS, depression misdiagnosed aging",
      }}
      badge="For Adults"
      heroTitle='Diagnosed With Depression at 60: This Is Not "Just Getting Older"'
      heroSubtitle="At 60, depression is the most misdiagnosed and undertreated it will ever be. Not because it's rare, but because everyone — including doctors and including you — is prepared to explain it away. If a doctor looked past all of that and told you that you have major depressive disorder, they did you a favor."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                At 60, depression is the most misdiagnosed and undertreated it
                will ever be. Not because it's rare, but because everyone,
                including doctors and including you, is prepared to explain it
                away. Low energy? Aging. Poor sleep? Aging. Loss of interest,
                trouble concentrating, unexplained aches? Aging, aging, aging.
              </p>
              <p>
                If a doctor looked past all of that and told you that you have
                major depressive disorder, they did you a favor. Now let's talk
                about what it means.
              </p>
            </div>
          ),
        },
        {
          heading: "What the diagnosis means at 60",
          body: (
            <div className="space-y-5">
              <p>
                It means the exhaustion, the flatness, the withdrawal, and the
                physical complaints that haven't responded to treatment are
                symptoms of an illness, not the inevitable texture of your
                sixties. Depression is not a normal part of aging. Most people
                in their sixties are not depressed. The ones who are have a
                treatable condition, and treatment works at this age as well as
                it works at any other.
              </p>
              <p>
                It does not mean you're losing your mind, that it's the
                beginning of dementia, or that there's nothing to be done.
                Depression in older adults is often mistaken for cognitive
                decline because it impairs memory and concentration. When the
                depression is treated, the thinking usually clears.
              </p>
            </div>
          ),
        },
        {
          heading: "Why 60 is a common time",
          body: (
            <div className="space-y-5">
              <p>
                Retirement, or its approach, removes the structure and identity
                that work provided for forty years. Friends and siblings begin
                to die. Health changes accumulate: heart disease, diabetes,
                chronic pain, and the medications for each, some of which
                contribute to depression. Adult children are busy with their own
                lives. Isolation creeps in quietly. And if depression has run in
                your family, or if you had episodes earlier in life that you
                pushed through, this is when the accumulated losses can bring it
                back or bring it out.
              </p>
            </div>
          ),
        },
        {
          heading: "What treatment looks like",
          body: (
            <div className="space-y-5">
              <p>
                Therapy and, when warranted, an antidepressant, chosen with
                attention to your other medications and conditions. Expect six
                to eight weeks before judging a medication. Rule out medical
                mimics: thyroid problems, vitamin deficiencies, sleep apnea,
                medication side effects. Stay physically and socially active;
                both matter more now.
              </p>
              <p>For many people at 60, this is enough.</p>
            </div>
          ),
        },
        {
          heading: "What to do when it isn't",
          body: (
            <div className="space-y-5">
              <p>
                Older adults are more likely to have depression that doesn't
                fully respond to medication, and more likely to have side
                effects that make medication hard to tolerate. If you've had two
                adequate trials and still aren't well, you have
                treatment-resistant depression, and the odds that a third
                medication produces remission are about 14 percent.
              </p>
              <p>
                That doesn't mean settling. Antidepressants work on brain
                chemistry. But depression is also a circuitry problem: the
                region behind your forehead that regulates mood, motivation, and
                attention is underactive, and no pill retrains it directly.
              </p>
            </div>
          ),
        },
        {
          heading: "A non-drug option that fits an older adult",
          body: (
            <div className="space-y-4">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an FDA-cleared, accelerated form of transcranial magnetic
                stimulation developed at Stanford's Brain Stimulation Lab. It's
                cleared for adults with major depressive disorder who haven't
                gotten adequate relief from antidepressants.
              </p>
              <p>
                For someone at 60, three things about it matter. It's not a
                drug, so it doesn't interact with the medications you're already
                taking, and those don't need to be stopped. It requires no
                anesthesia, unlike ECT. And it takes five days rather than the
                six-to-nine-week daily schedule of standard TMS.
              </p>
              <p>
                Here's how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> Our clinical team reviews your
                  history and medication trials and confirms you're a candidate.
                  The initial consultation is a Zoom call; as steps evolve,
                  you'll also have the option to meet by Zoom web conference or
                  in person. The mandatory in-person steps include fMRI mapping
                  and the actual treatment.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how your individual brain networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten 10-minute
                  sessions per day, with 50-minute breaks in between, for five
                  consecutive days.
                </li>
              </ol>

              <SaintComparisonStatBand />

              <p>
                In clinical trials of people who had failed medication, 79
                percent reached remission, with an average time to remission of
                2.6 days. Across more than 400 trial patients, 85 percent
                experienced symptom relief, with no serious adverse events. Side
                effects were mostly mild headache, fatigue, and scalp
                discomfort.
              </p>
              <p>
                SAINT isn't appropriate for people with non-removable metal in
                or near the head, such as certain surgical implants, or an
                implanted medical device like a pacemaker. That screening
                happens at consultation and is taken seriously at this age.
              </p>
            </div>
          ),
        },
        {
          heading: "What the next decades can look like",
          body: (
            <div className="space-y-5">
              <p>
                People in their sixties routinely have twenty or thirty years
                ahead. Those years can be spent in the flat, effortful version
                of life that depression produces, or they can be the years you
                finally have time for the things you always meant to do, with
                the energy and interest to enjoy them. That's not a fantasy.
                It's what remission looks like.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                Take the treatment you were given seriously and give it a fair
                trial. If you've done that and still aren't yourself, don't let
                anyone, including yourself, tell you this is just your age.
              </p>
              <p>
                At Neuro Wellness TMS Centers of America, a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>
                , the first appointment is an evaluation, not a commitment. We
                offer private outpatient care with a dedicated care team and
                weekend availability. We'll review your full medical and
                psychiatric picture and tell you honestly whether SAINT is right
                for you. Insurance coverage varies by plan; our team can help
                you understand your options, including prior authorization and
                self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, call or
                text 988 (Suicide &amp; Crisis Lifeline) or go to your nearest
                emergency room. This article is for educational purposes and is
                not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
